import { Fragment } from 'react';
import styled from 'styled-components';

import { translate } from '../helpers/translate.helper';
import { useEnums } from '../hooks/useEnums';
import { TooltipLarge } from '../TooltipLarge/TooltipLarge';
import { Box } from '../ui/Box';
import { theme } from '../ui/theme';

import { usePortDirectoryDetails } from './usePortDirectory';
import { formatNumber } from '../helpers/formatNumber.helper';
import { Characters } from '../helpers/unicode.helper';

const Icon = ({ name }) => <i className={`fas fa-${name}`} />;

const Pill = styled(Box).attrs((props) => ({
    display: 'inline-block',
    margin: 2,
    ...props,
}))`
    i + i {
        margin-left: 0.5ch;
    }

    cursor: pointer;

    color: ${theme.colors.foreground.subtle};
    background-color: transparent;

    padding: 1px 5px;
    font-size: 12px;
    line-height: 1.5;
    border-radius: 3px;

    border: 1px solid ${theme.colors.foreground.subtle};

    &:hover {
        background-color: ${theme.colors.foreground.subtle};
        color: #fff;
    }
`;

const Attribute = ({ tooltip, icon, children }) => {
    if (!tooltip) {
        return (
            <Pill>
                {icon ? <Icon name={icon} /> : null}
                {children}
            </Pill>
        );
    }

    return (
        <TooltipLarge title={tooltip.title} text={tooltip.text}>
            <Pill>
                {icon ? <Icon name={icon} /> : null}
                {children}
            </Pill>
        </TooltipLarge>
    );
};

const PortType = ({ portDirectory }) => {
    const { portType } = portDirectory;
    if (!portType) return null;

    const name = translate(`portdirectory.productrange.${portType}.name`);

    return (
        <Attribute
            tooltip={{
                title: name,
                text: translate(`portdirectory.productrange.${portType}.description`),
            }}
        >
            {name}
        </Attribute>
    );
};

const PortNoticeDays = ({ portDirectory }) => {
    const { noticeDays, bulkNotice } = portDirectory;
    if (!noticeDays) return null;

    const tooltipText = (
        <Fragment>
            {translate('portdirectory.generalnotice')} {formatNumber({ number: noticeDays })}
            {Characters.NO_BREAK_SPACE}
            {translate('portdirectory.workingdays')}
            {bulkNotice ? (
                <Fragment>
                    <br />
                    {[translate('portdirectory.bulknotice'), bulkNotice, translate('portdirectory.workingdays')].join(
                        ' '
                    )}
                </Fragment>
            ) : null}
        </Fragment>
    );

    return (
        <Attribute
            icon="calendar-alt"
            tooltip={{
                title: translate('portdirectory.noticerequired'),
                text: tooltipText,
            }}
        >
            {' '}
            {formatNumber({ number: noticeDays })}
        </Attribute>
    );
};

const PortDeliveryType = ({ portDirectory }) => {
    const {
        bulkAvailable,
        bulkMin,
        bulkMinote,
        ibcsAvailable,
        ibcsMin,
        ibcsMinNote,
        drumsPumpedAvailable,
        drumsPumpedMin,
        drumsPumpedMinNote,
        packedMin,
        packedMinNote,
    } = portDirectory;

    const text = [];
    const { getHumanReadableValue } = useEnums();

    if (bulkAvailable && (bulkMin || bulkMinote)) {
        // bulkMinote is a legacy typo and the correct naming
        text.push(
            <Fragment key="bulkmin">
                {translate('portdirectory.bulkmin')}
                {bulkMin
                    ? formatNumber({ number: bulkMin })
                    : getHumanReadableValue('portDirectoryMinNotes', bulkMinote)}
                <br />
            </Fragment>
        );
    }

    if (ibcsAvailable && (ibcsMin || ibcsMinNote)) {
        text.push(
            <Fragment key="ibcs">
                {translate('portdirectory.ibcsmin')}
                {ibcsMin ? ibcsMin : getHumanReadableValue('portDirectoryMinNotes', ibcsMinNote)}
                <br />
            </Fragment>
        );
    }

    if (drumsPumpedAvailable && (drumsPumpedMin || drumsPumpedMinNote)) {
        text.push(
            <Fragment key="drums">
                {translate('portdirectory.drumspumpedmin')}
                {drumsPumpedMin
                    ? formatNumber({ number: drumsPumpedMin })
                    : getHumanReadableValue('portDirectoryMinNotes', drumsPumpedMinNote)}
                <br />
            </Fragment>
        );
    }

    if (packedMin || packedMinNote) {
        text.push(
            <Fragment key="packed">
                {translate('portdirectory.packedmin')}
                {packedMin
                    ? formatNumber({ number: packedMin })
                    : getHumanReadableValue('portDirectoryMinNotes', packedMinNote)}
                <br />
            </Fragment>
        );
    }

    const characters = [
        bulkAvailable ? 'B' : null,
        ibcsAvailable ? 'T' : null,
        drumsPumpedAvailable ? 'DP' : null,
        'P',
    ].join(' ');

    return (
        <Attribute
            tooltip={{
                title: translate('portdirectory.deliverytype'),
                text,
            }}
        >
            {characters}
        </Attribute>
    );
};

const PortDeliveryMode = ({ portDirectory }) => {
    const { bargeAvailable, bargeMin, bargeMinNote, truckAvailable, truckMin, truckMinNote } = portDirectory;
    const { getHumanReadableValue } = useEnums();
    const text = [];

    if (!bargeAvailable && !truckAvailable) return null;

    if (bargeAvailable && (bargeMin || bargeMinNote)) {
        text.push(
            <Fragment>
                {translate('portdirectory.bargemin')}
                {bargeMin
                    ? formatNumber({ number: bargeMin })
                    : getHumanReadableValue('portDirectoryMinNotes', bargeMinNote)}
                <br />
            </Fragment>
        );
    }

    if (truckAvailable && (truckMin || truckMinNote)) {
        text.push(
            <Fragment>
                {translate('portdirectory.truckmin')}
                {truckMin
                    ? formatNumber({ number: truckMin })
                    : getHumanReadableValue('portDirectoryMinNotes', truckMinNote)}
                <br />
            </Fragment>
        );
    }

    return (
        <Attribute
            tooltip={{
                title: translate('portdirectory.deliverymode'),
                text,
            }}
        >
            {bargeAvailable ? <Icon name="ship" /> : null}
            {truckAvailable ? <Icon name="truck" /> : null}
        </Attribute>
    );
};

const PortNotesAndCharges = ({ portDirectory }) => {
    const { notes, charges } = portDirectory;
    if (!notes && !charges) return null;

    return (
        <Attribute
            tooltip={{
                title: translate('portdirectory.notesandcharges.title'),
                text: (
                    <Fragment>
                        {notes ? (
                            <Fragment>
                                {notes}
                                <br />
                            </Fragment>
                        ) : null}
                        {charges}
                    </Fragment>
                ),
            }}
        >
            <Icon name="file-alt" />
            <Icon name="dollar-sign" />
        </Attribute>
    );
};

export const PortDirectory = ({ portId, supplierGroupId }) => {
    const { data, isPending, isError } = usePortDirectoryDetails({
        portId,
        supplierGroupId,
    });

    if (isPending || isError) {
        return null;
    }

    return (
        <Box>
            <PortType portDirectory={data} />
            <PortNoticeDays portDirectory={data} />
            <PortNotesAndCharges portDirectory={data} />
            <PortDeliveryMode portDirectory={data} />
            <PortDeliveryType portDirectory={data} />
        </Box>
    );
};
